import {
    Box,
    Stack,
    Typography,
    Button,
    TextField,
    MenuItem,
    Alert,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Divider,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useMemo, useState } from "react";

import { HttpCache, HttpUserClient } from "../../http";
import { EntityWithIdAndDescription } from "../../types/baseEntities";
import { UserClientCreateUpdateDTO, UserClientFields } from "../../types/user/userClient";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { getCouponFieldStyle } from "../Checkout/CouponStyles";
import { useTheme } from "../../util/themes";

/* =========================
   Constantes dominio
========================= */
const DOC_DNI = 1;
const DOC_PASAPORTE = 2;
const DOC_CUIT = 3;

const PERSONA_FISICA = 2;
const PERSONA_JURIDICA = 3;

/* =========================
   Helpers
========================= */
const onlyDigits = (v: string) => (v ?? "").replace(/\D/g, "");

const getTipoClientePorCuit = (cuitRaw: string): number => {
    const cuit = onlyDigits(cuitRaw);
    if (cuit.length < 2) return PERSONA_FISICA;

    const pref = parseInt(cuit.substring(0, 2), 10);
    if ([20, 23, 24, 27].includes(pref)) return PERSONA_FISICA;
    return PERSONA_JURIDICA;
};

/* =========================
   Types & schema
========================= */
export type RegisterClientForm = {
    sameAsUser: boolean;
    codDocumentoTipo: number;
    numeroDocumento: string;
    codPersonaTipo: number;
    nombre?: string | null;
    apellido?: string | null;
    razonSocial?: string | null;
};

const schema: yup.ObjectSchema<RegisterClientForm> = yup.object({
    sameAsUser: yup.boolean().required(),

    codDocumentoTipo: yup
        .number()
        .required("Debe seleccionar un tipo de documento")
        .oneOf([DOC_DNI, DOC_CUIT], "Tipo de documento inválido"),

    numeroDocumento: yup
        .string()
        .required("Debe ingresar el número de documento")
        .test("digits", "Solo números", (v) => onlyDigits(v ?? "").length > 0),

    codPersonaTipo: yup.number().required(),

    nombre: yup
        .string()
        .nullable()
        .when("codPersonaTipo", {
            is: PERSONA_FISICA,
            then: (s) => s.required("El nombre es obligatorio").nullable(),
            otherwise: (s) => s.nullable(),
        }),

    apellido: yup
        .string()
        .nullable()
        .when("codPersonaTipo", {
            is: PERSONA_FISICA,
            then: (s) => s.required("El apellido es obligatorio").nullable(),
            otherwise: (s) => s.nullable(),
        }),

    razonSocial: yup
        .string()
        .nullable()
        .when("codPersonaTipo", {
            is: PERSONA_JURIDICA,
            then: (s) => s.required("La razón social es obligatoria").nullable(),
            otherwise: (s) => s.nullable(),
        }),
});

/* =========================
   Props
========================= */
interface ClientCreateFormProps {
    prefillUser?: { nombre: string; apellido: string };
    onSuccess: () => void;
    onLoadingChange?: (loading: boolean) => void;
}

const TEXT = {
    cardTitle: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 700,
    },
    meta: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.85,
    },
} as const;

/* =========================
   Component
========================= */
export const ClientCreateForm = ({
    prefillUser,
    onSuccess,
    onLoadingChange,
}: ClientCreateFormProps) => {
    const theme = useTheme();
    const couponFieldStyle = getCouponFieldStyle(theme as any);

    const [docTypes, setDocTypes] = useState<EntityWithIdAndDescription[]>([]);
    const [personTypes, setPersonTypes] = useState<EntityWithIdAndDescription[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, watch, setValue } = useForm<RegisterClientForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            sameAsUser: !!prefillUser,
            codDocumentoTipo: DOC_DNI,
            numeroDocumento: "",
            codPersonaTipo: PERSONA_FISICA,
            nombre: prefillUser?.nombre ?? "",
            apellido: prefillUser?.apellido ?? "",
            razonSocial: prefillUser ? `${prefillUser.apellido}-${prefillUser.nombre}` : "",
        },
    });

    const sameAsUser = watch("sameAsUser");
    const codDocumentoTipo = watch("codDocumentoTipo");
    const numeroDocumento = watch("numeroDocumento");
    const codPersonaTipo = watch("codPersonaTipo");

    useEffect(() => {
        const loadCache = async () => {
            const [docs, persons] = await Promise.all([
                HttpCache.getIdentityDocumentTypes(),
                HttpCache.getPersonTypes(),
            ]);

            setDocTypes((docs ?? []).filter((d) => d.id !== DOC_PASAPORTE));
            setPersonTypes(persons ?? []);
        };

        loadCache();
    }, []);

    useEffect(() => {
        if (sameAsUser && prefillUser) {
            setValue("codDocumentoTipo", DOC_DNI);
            setValue("codPersonaTipo", PERSONA_FISICA);
            setValue("nombre", prefillUser.nombre);
            setValue("apellido", prefillUser.apellido);
            setValue("razonSocial", `${prefillUser.apellido}-${prefillUser.nombre}`);
            return;
        }

        if (codDocumentoTipo === DOC_DNI) setValue("codPersonaTipo", PERSONA_FISICA);

        if (codDocumentoTipo === DOC_CUIT) {
            const tipo = getTipoClientePorCuit(numeroDocumento);
            setValue("codPersonaTipo", tipo);
        }

        if (codPersonaTipo === PERSONA_JURIDICA) {
            setValue("nombre", null);
            setValue("apellido", null);
        }
    }, [sameAsUser, codDocumentoTipo, numeroDocumento, codPersonaTipo, prefillUser, setValue]);

    const personaTipoDesc = useMemo(() => {
        const p = personTypes.find((x) => x.id === codPersonaTipo);
        return p?.descripcion ?? "";
    }, [codPersonaTipo, personTypes]);

    const docTipoDesc = useMemo(() => {
        const d = docTypes.find((x) => x.id === codDocumentoTipo);
        return d?.descripcion ?? "";
    }, [codDocumentoTipo, docTypes]);

    const onSubmit = async (data: RegisterClientForm) => {
        setError("");
        setLoading(true);
        onLoadingChange?.(true);

        try {
            const docNumber = onlyDigits(data.numeroDocumento);

            let personaTipo = data.codPersonaTipo;
            let nombre = data.nombre ?? null;
            let apellido = data.apellido ?? null;
            let razonSocial: string;

            if (data.sameAsUser && prefillUser) {
                personaTipo = PERSONA_FISICA;
                nombre = prefillUser.nombre;
                apellido = prefillUser.apellido;
                razonSocial = `${apellido}-${nombre}`;
            } else {
                if (data.codDocumentoTipo === DOC_CUIT) {
                    personaTipo = getTipoClientePorCuit(docNumber);
                }

                if (personaTipo === PERSONA_FISICA) {
                    razonSocial = `${apellido ?? ""}-${nombre ?? ""}`.trim();
                } else {
                    razonSocial = (data.razonSocial ?? "").trim();
                    nombre = null;
                    apellido = null;
                }
            }

            const dto: UserClientCreateUpdateDTO = {
                [UserClientFields.PersonTypeId]: personaTipo,
                [UserClientFields.IdentityDocumentTypeId]: data.codDocumentoTipo,
                [UserClientFields.IdentityDocumentNumber]: docNumber,
                [UserClientFields.Name]: personaTipo === PERSONA_FISICA ? nombre : null,
                [UserClientFields.Surname]: personaTipo === PERSONA_FISICA ? apellido : null,
                [UserClientFields.CompanyName]: razonSocial,
            };

            const resp = await HttpUserClient.createClient(dto);

            if ((resp as any).tieneError) {
                setError((resp as any).descripcionError);
                return;
            }

            onSuccess();
        } catch (e: any) {
            setError(e?.message ?? "Error al crear cliente");
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <Typography sx={{ ...TEXT.cardTitle, mb: 1 }}>Datos del cliente</Typography>
            <Typography sx={{ ...TEXT.meta, mb: 2 }}>
                Este cliente quedará asociado a tu usuario.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
                {prefillUser && (
                    <Controller
                        name="sameAsUser"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        ...TYPOGRAPHY_STYLES.rubikSmall,
                                        fontWeight: 600,
                                    },
                                }}
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Usar los mismos datos del usuario"
                            />
                        )}
                    />
                )}

                <Controller
                    name="codDocumentoTipo"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            select
                            label="Tipo de documento"
                            fullWidth
                            disabled={sameAsUser || loading}
                            {...field}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={couponFieldStyle}
                        >
                            <MenuItem value={DOC_DNI}>DNI</MenuItem>
                            <MenuItem value={DOC_CUIT}>CUIT</MenuItem>
                        </TextField>
                    )}
                />

                <Controller
                    name="numeroDocumento"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextField
                            label={`Número ${docTipoDesc ? `(${docTipoDesc})` : ""}`}
                            fullWidth
                            disabled={loading}
                            {...field}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={couponFieldStyle}
                        />
                    )}
                />

                {codPersonaTipo === PERSONA_FISICA && (
                    <>
                        <Controller
                            name="nombre"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Nombre"
                                    fullWidth
                                    disabled={sameAsUser || loading}
                                    {...field}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    sx={couponFieldStyle}
                                />
                            )}
                        />

                        <Controller
                            name="apellido"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    label="Apellido"
                                    fullWidth
                                    disabled={sameAsUser || loading}
                                    {...field}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    sx={couponFieldStyle}
                                />
                            )}
                        />
                    </>
                )}

                {codPersonaTipo === PERSONA_JURIDICA && (
                    <Controller
                        name="razonSocial"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="Razón social"
                                fullWidth
                                disabled={loading}
                                {...field}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                sx={couponFieldStyle}
                            />
                        )}
                    />
                )}

                <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    sx={{ ...BUTTON_STYLES.invertedPrimary, width: "100%" }}
                >
                    Guardar cliente
                </Button>

                {error && <Alert severity="error">{error}</Alert>}
            </Stack>
        </Box>
    );
};
